import _ from 'lodash'

import Logger from '@/logger'
import AxiosBuilder from './axios-builder'
import REQUEST_HEADERS from './request-headers'

/**
 * Base for services.
 * @export Service
 * @class Service
 */
export default class Service {

    /**
     * Creates an instance of Service.
     * @param {string} baseUrl The base URL for the service.
     * @param {string} loggingCategory The logging category to use.
     * @memberof Service
     */
    constructor(baseUrl = '/', loggingCategory) {
        this.baseUrl = baseUrl
        this.loggingCategory = loggingCategory

        /**
         * @type {AxiosInstance}
         */
        this.axios = AxiosBuilder.build(this.baseUrl)
    }

    /**
     * Create an Axios config object for each request.
     * @returns {Object}
     * @protected
     */
    _buildAxiosConfig() {
        return {
            headers: AxiosBuilder.buildHeaders([ REQUEST_HEADERS.BEARER_TOKEN, REQUEST_HEADERS.SESSION_TOKEN ]),
        }
    }

    /**
     * Handles a failed operation.
     * @param {Error|string} error An error or error message
     * @param {ServiceOperation} operationState
     * @param {string} methodName The name of the calling method.
     * @param {number} statusCode Defaults to 500 for a failed operation.
     * @returns {ServiceOperation} The modified operation state.
     * @memberof Service
     * @protected
     */
    _failOperation(error, operationState, methodName = '?', statusCode = 500) {
        if (error.response && error.response.statusCode)
            statusCode = error.response.statusCode

        operationState.fail(error, statusCode)
        Logger.logObject(operationState, `Data service failed [${ methodName }]`, this.loggingCategory)
        return operationState
    }

    /**
     * Handles a successful data load [GET] and processes the response safely.
     * @param {Object} response The service response.
     * @param {ServiceOperation} operationState
     * @param {string} methodName The name of the calling method.
     * @param {Function?} postProcess An optional function to process the data.
     * @returns {ServiceOperation} The modified operation state.
     * @memberof Service
     * @protected
     */
    _processData(response, operationState, methodName = '?', postProcess) {
        if (response.data !== false && !response.data || response.data == null) {
            Logger.log(`No Data returned [${ methodName }]`, this.loggingCategory)
            operationState.complete(false)
        } else {
            const output = postProcess && _.isFunction(postProcess)
                ? postProcess(response.data) : response.data
            Logger.logObject({ object: output, json: JSON.stringify(output), rawApiData: response.data }, `Data returned [${ methodName }]`, this.loggingCategory)
            operationState.complete(true, output)
        }

        return operationState
    }

    /**
     * Handles a successful data changing operation and processes the response safely.
     * @param {Object} response The service response.
     * @param {ServiceOperation} operationState
     * @param {string} methodName The name of the calling method.
     * @returns {ServiceOperation} The modified operation state.
     * @memberof Service
     * @protected
     */
    _processResponse(response, operationState, methodName = '?') {
        if (response.data !== false && !response.data || response.data == null)
            operationState.complete(true, null, response.status)
        else
            operationState.complete(true, response.data, response.status)

        Logger.logObject(response, `Operation completed [${ methodName }]`, this.loggingCategory)

        return operationState
    }

    /**
     * DELETEs data from the given API endpoint and processes the response.
     * @param {string} url The URI for the API endpoint to POST to.
     * @param {ServiceOperation} operationState
     * @param {string} methodName The name of the calling method.
     * @returns {Promise<ServiceOperation>} The modified operation state. Successful if the operation works as intended or failed if not.
     * @memberof Service
     * @protected
     */
    async _processDelete(url, operationState, methodName = '?') {
        try {
            const deleteResponse = await this.axios.delete(url, this._buildAxiosConfig())
            Logger.logObject(deleteResponse, `delete response`, this.loggingCategory)
            return this._processResponse(deleteResponse, operationState, methodName)
        } catch (err) {
            return this._failOperation(err, operationState, methodName)
        }
    }

    /**
     * GETs data from a given API endpoint and processes the response.
     * @param {string} url The URI for the API endpoint to GET from.
     * @param {ServiceOperation} operationState
     * @param {string} methodName The name of the calling method.
     * @param {Function?} postProcess An optional function to process the data.
     * @returns {Promise<ServiceOperation>} The modified operation state. Successful if the operation works as intended or failed if not.
     * @memberof Service
     * @protected
     */
    async _processGet(url, operationState, methodName = '?', postProcess) {
        try {
            const getResponse = await this.axios.get(url, this._buildAxiosConfig())
            Logger.logObject(getResponse, `response`, this.loggingCategory)
            return this._processData(getResponse, operationState, methodName, postProcess)
        } catch (err) {
            return this._failOperation(err, operationState, methodName)
        }
    }

    /**
     * POSTs data to the given API endpoint and processes the response.
     * @param {string} url The URI for the API endpoint to POST to.
     * @param {Object} data
     * @param {ServiceOperation} operationState
     * @param {string} methodName The name of the calling method.
     * @returns {Promise<ServiceOperation>} The modified operation state. Successful if the operation works as intended or failed if not.
     * @memberof Service
     * @protected
     */
    async _processPost(url, data, operationState, methodName = '?') {
        try {
            const postResponse = await this.axios.post(url, data, this._buildAxiosConfig())
            Logger.logObject(postResponse, `post response`, this.loggingCategory)
            return this._processResponse(postResponse, operationState, methodName)
        } catch (err) {
            return this._failOperation(err, operationState, methodName)
        }
    }

    /**
     * PUTs data to the given API endpoint and processes the response.
     * @param {string} url The URI for the API endpoint to PUT to.
     * @param {Object} data
     * @param {ServiceOperation} operationState
     * @param {string} methodName The name of the calling method.
     * @returns {Promise<ServiceOperation>} The modified operation state. Successful if the operation works as intended or failed if not.
     * @memberof Service
     * @protected
     */
    async _processPut(url, data, operationState, methodName = '?') {
        try {
            const putResponse = await this.axios.put(url, data, this._buildAxiosConfig())
            Logger.logObject(putResponse, `put response`, this.loggingCategory)
            return this._processResponse(putResponse, operationState, methodName)
        } catch (err) {
            return this._failOperation(err, operationState, methodName)
        }
    }
}
